import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../LoadingSpinner';
import { formatDuration, formatNumber, getSeason } from '../../utils/format';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExploreIcon from '@mui/icons-material/Explore';

interface YearWrappedData {
  year: number;
  totalTime: number;
  songsPlayed: number;
  songsCompleted: number;
  uniqueSongs: number;
  activeDays: number;
  topArtist: {
    artist_name: string;
    play_count: number;
    total_time: number;
  } | null;
  topSong: {
    title: string;
    artist_name: string;
    play_count: number;
  } | null;
  monthlyStats: Array<{
    month: number;
    total_time: number;
    stream_count: number;
  }>;
  newSongsDiscovered: number;
  newArtistsDiscovered: number;
}

interface YearWrappedProps {
  onClose: () => void;
}

export function YearWrapped({ onClose }: YearWrappedProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());

  const { data, isLoading, error } = useQuery<YearWrappedData>({
    queryKey: ['analytics', 'wrapped', year],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/analytics/wrapped?year=${year}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch wrapped data');
      }
      
      return response.json();
    },
  });

  const slides = [
    'intro',
    'totalTime',
    'topArtist',
    'topSong',
    'discovery',
    'patterns',
    'summary'
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const getTopMonth = () => {
    if (!data?.monthlyStats?.length) return null;
    return data.monthlyStats.reduce((top, month) => 
      month.total_time > top.total_time ? month : top
    );
  };

  const getListeningPersonality = () => {
    if (!data) return 'Music Explorer';
    
    const avgPerDay = data.totalTime / 365;
    const completionRate = data.songsCompleted / data.songsPlayed;
    
    if (avgPerDay > 7200) return 'Music Devotee'; // 2+ hours per day
    if (completionRate > 0.8) return 'Focused Listener';
    if (data.newSongsDiscovered > 100) return 'Discovery Machine';
    if (data.activeDays > 300) return 'Daily Listener';
    return 'Music Explorer';
  };

  const handleShare = async () => {
    if (navigator.share && data) {
      try {
        await navigator.share({
          title: `My ${year} Music Year in Review`,
          text: `I listened to ${formatDuration(data.totalTime)} of music in ${year}! Check out Naia Web Player.`,
          url: window.location.origin
        });
      } catch (err) {
        // Fall back to copying to clipboard
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    if (data) {
      const text = `My ${year} Music Year in Review on Naia Web Player:\n• ${formatDuration(data.totalTime)} of listening time\n• ${data.topArtist?.artist_name || 'Various'} was my top artist\n• Discovered ${data.newSongsDiscovered} new songs\nCheck it out at ${window.location.origin}`;
      navigator.clipboard.writeText(text);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-dark-void p-8 rounded-xl">
          <LoadingSpinner />
          <p className="text-pearl-white mt-4">Preparing your year in music...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-dark-void p-8 rounded-xl max-w-md text-center">
          <h3 className="text-xl font-semibold text-pearl-white mb-4">No Data Available</h3>
          <p className="text-slate-grey mb-6">
            We don't have enough listening data for {year} to create your Year Wrapped.
          </p>
          <div className="flex gap-4">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="bg-white/10 border border-white/20 rounded px-3 py-2 text-pearl-white"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  const renderSlide = () => {
    const slideType = slides[currentSlide];
    const topMonth = getTopMonth();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    switch (slideType) {
      case 'intro':
        return (
          <div className="text-center">
            <div className="mb-8">
              <div className="text-6xl mb-4">🎵</div>
              <h2 className="text-4xl font-bold text-pearl-white mb-4">
                Your {year}
              </h2>
              <h3 className="text-2xl text-naia-pink mb-6">
                Year in Music
              </h3>
              <p className="text-slate-grey text-lg">
                Let's take a journey through your musical year
              </p>
            </div>
          </div>
        );

      case 'totalTime':
        return (
          <div className="text-center">
            <TimerIcon className="text-6xl text-naia-pink mb-6" />
            <h2 className="text-3xl font-bold text-pearl-white mb-4">
              You listened to
            </h2>
            <div className="text-6xl font-bold bg-gradient-to-r from-naia-pink to-soft-violet bg-clip-text text-transparent mb-6">
              {formatDuration(data.totalTime)}
            </div>
            <p className="text-slate-grey text-lg">
              of music in {year}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-slate-grey">Songs Played</p>
                <p className="text-xl font-bold text-pearl-white">{formatNumber(data.songsPlayed)}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-slate-grey">Active Days</p>
                <p className="text-xl font-bold text-pearl-white">{data.activeDays}</p>
              </div>
            </div>
          </div>
        );

      case 'topArtist':
        return (
          <div className="text-center">
            <PersonIcon className="text-6xl text-cyber-teal mb-6" />
            <h2 className="text-3xl font-bold text-pearl-white mb-4">
              Your top artist was
            </h2>
            <div className="text-4xl font-bold text-cyber-teal mb-4">
              {data.topArtist?.artist_name || 'Various Artists'}
            </div>
            {data.topArtist && (
              <>
                <p className="text-slate-grey text-lg mb-6">
                  You played their music {data.topArtist.play_count} times
                </p>
                <div className="bg-gradient-to-r from-cyber-teal/20 to-electric-blue/20 rounded-lg p-6">
                  <p className="text-sm text-slate-grey mb-2">Total listening time</p>
                  <p className="text-2xl font-bold text-pearl-white">
                    {formatDuration(data.topArtist.total_time)}
                  </p>
                </div>
              </>
            )}
          </div>
        );

      case 'topSong':
        return (
          <div className="text-center">
            <MusicNoteIcon className="text-6xl text-soft-violet mb-6" />
            <h2 className="text-3xl font-bold text-pearl-white mb-4">
              Your most played song
            </h2>
            {data.topSong ? (
              <>
                <div className="text-3xl font-bold text-soft-violet mb-2">
                  "{data.topSong.title}"
                </div>
                <p className="text-xl text-slate-grey mb-6">
                  by {data.topSong.artist_name}
                </p>
                <div className="bg-gradient-to-r from-soft-violet/20 to-naia-pink/20 rounded-lg p-6">
                  <p className="text-sm text-slate-grey mb-2">Times played</p>
                  <p className="text-3xl font-bold text-pearl-white">
                    {data.topSong.play_count}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-slate-grey text-lg">
                You explored many different songs this year!
              </p>
            )}
          </div>
        );

      case 'discovery':
        return (
          <div className="text-center">
            <ExploreIcon className="text-6xl text-electric-blue mb-6" />
            <h2 className="text-3xl font-bold text-pearl-white mb-6">
              Musical Discovery
            </h2>
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-gradient-to-r from-electric-blue/20 to-cyber-teal/20 rounded-lg p-6">
                <p className="text-4xl font-bold text-electric-blue mb-2">
                  {data.newSongsDiscovered}
                </p>
                <p className="text-slate-grey">New songs discovered</p>
              </div>
              <div className="bg-gradient-to-r from-cyber-teal/20 to-soft-violet/20 rounded-lg p-6">
                <p className="text-4xl font-bold text-cyber-teal mb-2">
                  {data.newArtistsDiscovered}
                </p>
                <p className="text-slate-grey">New artists discovered</p>
              </div>
            </div>
            <p className="text-slate-grey text-lg mt-6">
              You're a true music explorer! 🎵
            </p>
          </div>
        );

      case 'patterns':
        return (
          <div className="text-center">
            <TrendingUpIcon className="text-6xl text-naia-pink mb-6" />
            <h2 className="text-3xl font-bold text-pearl-white mb-6">
              Your Listening Patterns
            </h2>
            <div className="space-y-6">
              {topMonth && (
                <div className="bg-gradient-to-r from-naia-pink/20 to-soft-violet/20 rounded-lg p-6">
                  <p className="text-lg text-slate-grey mb-2">Your biggest music month</p>
                  <p className="text-3xl font-bold text-naia-pink">
                    {monthNames[topMonth.month - 1]}
                  </p>
                  <p className="text-sm text-slate-grey mt-2">
                    {formatDuration(topMonth.total_time)} listened
                  </p>
                </div>
              )}
              <div className="bg-white/5 rounded-lg p-6">
                <p className="text-lg text-slate-grey mb-2">Your listening personality</p>
                <p className="text-2xl font-bold text-pearl-white">
                  {getListeningPersonality()}
                </p>
              </div>
            </div>
          </div>
        );

      case 'summary':
        return (
          <div className="text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-3xl font-bold text-pearl-white mb-6">
              That's a wrap on {year}!
            </h2>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-r from-naia-pink/20 to-soft-violet/20 rounded-lg p-4">
                <p className="text-2xl font-bold text-naia-pink">{formatDuration(data.totalTime)}</p>
                <p className="text-sm text-slate-grey">Total time</p>
              </div>
              <div className="bg-gradient-to-r from-cyber-teal/20 to-electric-blue/20 rounded-lg p-4">
                <p className="text-2xl font-bold text-cyber-teal">{data.uniqueSongs}</p>
                <p className="text-sm text-slate-grey">Unique songs</p>
              </div>
              <div className="bg-gradient-to-r from-soft-violet/20 to-naia-pink/20 rounded-lg p-4">
                <p className="text-2xl font-bold text-soft-violet">{data.newSongsDiscovered}</p>
                <p className="text-sm text-slate-grey">Songs discovered</p>
              </div>
              <div className="bg-gradient-to-r from-electric-blue/20 to-cyber-teal/20 rounded-lg p-4">
                <p className="text-2xl font-bold text-electric-blue">{data.activeDays}</p>
                <p className="text-sm text-slate-grey">Active days</p>
              </div>
            </div>
            <p className="text-slate-grey">
              Thanks for making {year} musical with Naia Web Player! 🎵
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-dark-void to-slate-800 rounded-2xl max-w-lg w-full mx-4 relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-4">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="bg-white/10 border border-white/20 rounded px-3 py-1 text-pearl-white text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <Button onClick={handleShare} variant="outline" size="sm">
              <ShareIcon className="mr-1 text-sm" />
              Share
            </Button>
            <Button onClick={onClose} variant="outline" size="sm">
              <CloseIcon className="text-sm" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 min-h-[400px] flex flex-col justify-center">
          {renderSlide()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <Button
            onClick={prevSlide}
            variant="outline"
            disabled={currentSlide === 0}
            className="flex items-center"
          >
            <ChevronLeftIcon className="mr-1" />
            Previous
          </Button>

          {/* Slide indicators */}
          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-naia-pink' : 'bg-white/30'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={nextSlide}
            variant="outline"
            disabled={currentSlide === slides.length - 1}
            className="flex items-center"
          >
            Next
            <ChevronRightIcon className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
} 